import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {Helmet} from "react-helmet-async";

export default function JobDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    // Apply Now Click Handler
    const handleApplyClick = () => {
        navigate(`/career/apply/${job.id}`); // কোডের পরিবর্তে জবের id দিয়ে রাউট করা নিরাপদ
    };

    // WhatsApp Click Handler
    const handleWhatsAppClick = () => {
        const phoneNumber = "+8801700000000"; // আপনার কোম্পানির হোয়াটসঅ্যাপ নাম্বার এখানে দিন
        const message = `Hello, I am interested in applying for the job: *${job.title}* (Code: ${job.code}). Please share more details.`;
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const API_URL = process.env.REACT_APP_API_BASE_URL;
    const CAREERS_ENDPOINT = process.env.REACT_APP_API_CAREERS;

    useEffect(() => {
        axios.get(`${API_URL}${CAREERS_ENDPOINT}/${id}`)
            .then(res => {
                setJob(res.data.data || res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching job details:", err);
                setLoading(false);
            });
    }, [id]);

    if (loading) {
        return <div className="text-center py-5">Loading job details...</div>;
    }

    if (!job) {
        return <div className="text-center py-5 text-danger">Job not found!</div>;
    }

    return (
        <>
            <Helmet>
                <title>Tarif Overseas Ltd. | {job.title}</title>
                <meta name="description" content="Tarif Overseas Ltd. is a trusted overseas recruitment agency in Bangladesh providing professional manpower recruitment services."/>
                <link rel="canonical" href={`https://tarifoverseasltd.com/career/${job.code}`}/>
            </Helmet>
            <div className="min-vh-100 bg-light d-flex flex-column font-sans text-dark">
            <main className="container my-4 flex-grow-1">
                <div className="row g-4">
                    {/* Left Column (Job Details Content) */}
                    <div className="col-lg-8">
                        {/* Header Box */}
                        <div className="card shadow-sm border p-4 mb-4 bg-white">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                                <div>
                                    <h1 className="fw-bold fs-3 text-dark mb-2">{job.title}</h1>
                                    <div className="d-flex gap-2 flex-wrap mb-3">
                                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1">{job.country?.name}</span>
                                        <span className="badge bg-warning bg-opacity-10 text-dark border border-warning border-opacity-25 px-2.5 py-1">{job.category?.name}</span>
                                        <span className="badge bg-secondary bg-opacity-10 text-secondary border px-2.5 py-1">
                                            {job.job_type ? job.job_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-lg-end">
                                    <div className="text-success fw-bold fs-4">{job.salary}</div>
                                    <div className="text-muted small">per month</div>
                                </div>
                            </div>

                            {/* Feature / Benefits Tags */}
                            <div className="d-flex flex-wrap gap-2 pt-2 border-top">
                                {job.benefits_tags && job.benefits_tags.split(',').map((tag, idx) => (
                                    <span key={idx} className="badge bg-success bg-opacity-10 text-success fw-normal px-2 py-1">✓ {tag.trim()}</span>
                                ))}
                            </div>
                        </div>

                        {/* Job Description Section */}
                        <div className="card shadow-sm border p-4 mb-4 bg-white">
                            <h4 className="fw-bold fs-5 mb-3 text-dark border-bottom pb-2">📋 Job Description</h4>
                            <div className="small text-secondary space-y-3 lh-lg">
                                <p><strong>Contract:</strong> {job.contract}</p>
                                <p><strong>Region / Location:</strong> {job.region}</p>
                                <p><strong>Gender:</strong> {job.gender}</p>
                                <p><strong>Deadline:</strong> {job.deadline}</p>

                                <div className="mt-3" dangerouslySetInnerHTML={{ __html: job.description }} />

                                {job.additional_requirements && (
                                    <>
                                        <h5 className="fw-bold text-dark mt-4 mb-2 fs-6">Additional Requirements:</h5>
                                        <div dangerouslySetInnerHTML={{ __html: job.additional_requirements }} />
                                    </>
                                )}

                                {job.compensation_other_benefits && (
                                    <>
                                        <h5 className="fw-bold text-dark mt-4 mb-2 fs-6">Compensation & Other Benefits:</h5>
                                        <div dangerouslySetInnerHTML={{ __html: job.compensation_other_benefits }} />
                                    </>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Right Column (Sticky Summary & Apply Card) */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm border p-4 bg-white sticky-top" style={{ top: '20px', zIndex: '50' }}>
                            <div className="text-center mb-3 pb-3 border-bottom">
                                <div className="text-success fw-bold fs-4">{job.salary}</div>
                                <div className="text-muted small">per month</div>
                            </div>

                            <div className="d-grid gap-2 mb-4">
                                <button
                                    onClick={handleApplyClick}
                                    className="btn btn-warning text-white fw-bold py-2 shadow-sm"
                                    style={{ backgroundColor: '#f59e0b' }}
                                >
                                    🚀 Apply Now
                                </button>
                                <button
                                    onClick={handleWhatsAppClick}
                                    className="btn btn-outline-success fw-semibold py-2"
                                >
                                    💬 WhatsApp
                                </button>
                            </div>

                            <div className="small text-muted text-center mb-4">
                                ⏳ Deadline: {job.deadline}
                            </div>

                            <hr className="my-3" />

                            <h6 className="fw-bold text-dark mb-3 text-uppercase" style={{ fontSize: '12px' }}>Job Summary</h6>

                            <div className="small space-y-2">
                                <div className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Country</span>
                                    <span className="fw-semibold text-dark">{job.country?.name}</span>
                                </div>
                                <div className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Region</span>
                                    <span className="fw-semibold text-dark">{job.region}</span>
                                </div>
                                <div className="d-flex justify-content-between py-1 border-bottom">1
                                    <span className="text-muted">Category</span>
                                    <span className="fw-semibold text-dark">{job.category?.name}</span>
                                </div>
                                <div className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Job Type</span>
                                    <span className="fw-semibold text-dark">{job.job_type ? job.job_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''}</span>
                                </div>
                                <div className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Gender</span>
                                    <span className="fw-semibold text-dark">{job.gender ? job.gender.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '' }</span>
                                </div>
                                <div className="d-flex justify-content-between py-1">
                                    <span className="text-muted">Job Code</span>
                                    <span className="fw-semibold text-dark">{job.code}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Back Button */}
            <div className="container mb-4">
                <button onClick={() => navigate(-1)} className="btn btn-outline-secondary btn-sm">
                    ← Back to All Jobs
                </button>
            </div>
        </div>
        </>
    );
}